<i18n src="@/locales/modules/UserSettingsTranslate.json"></i18n>

<template>
	<div class="custom-dialog">
		<div class="bg-gray-500 p-1 my-4">
			<div class="bg-white p-4 border rounded-b">
				<div class="font-bold text-center mb-3">
					{{ $t('ProfileDormitory.PersonalInfo') }}
				</div>
				<div class="grid grid-cols-1 sm:grid-cols-2 gap:2 sm:gap-4">
					<div class="mb-5">
						<label for="fullname" class="vs-input--label">
							{{ $t('ProfileDormitory.FullName') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							id="fullname"
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.FullName')}`"
							v-model="localData.personal_info.full_name"
							class="w-full"
							name="fullname"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.FullName')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('fullname')">{{ errors.first("fullname") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Gender') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.Gender').toLowerCase()}`"
							v-model="localData.personal_info.gender"
							name="gender"
							class="w-full"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Gender')"
							:options="choices.genders"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('gender')">{{ errors.first("gender") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-3 gap:2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.DateOfBirth') }}
							<span class="text-danger">*</span>:
						</label>
						<vx-input-group>
							<template slot="append">
								<div class="append-text text-black">
									<i class="nucleo nucleo-calendar-date" @click="triggerClickDateOfBirthPicker"></i>
								</div>
							</template>

							<cbs-date-picker
								clearable
								class="w-full"
								name="dateofbirth"
								prefix-icon="''"
								ref="dateOfBirthPickerRef"
								:format="userDateFormat"
								value-format="yyyy-MM-dd"
								:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.DateOfBirth').toLowerCase()}`"
								v-model="localData.personal_info.date_of_birth"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.DateOfBirth')"
							/>
						</vx-input-group>
						<span class="text-danger text-sm" v-show="errors.has('dateofbirth')">{{ errors.first("dateofbirth") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.PlaceOfBirth') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.PlaceOfBirth').toLowerCase()}`"
							v-model="localData.personal_info.place_of_birth"
							:reduce="state => state.name"
							label="name"
							name="placeofbirth"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.PlaceOfBirth')"
							:options="this.$store.state.states"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('placeofbirth')">{{ errors.first("placeofbirth") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Hometown') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.State').toLowerCase()}`"
							v-model="localData.personal_info.hometown"
							:reduce="state => state.name"
							label="name"
							name="hometown"
							class="mb-3 sm:mb-0"
							v-validate="'required'"
							:options="this.$store.state.states"
							@input="handleStateChange"
							:data-vv-as="$t('ProfileDormitory.State')"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('hometown')">{{ errors.first("hometown") }}</span>
					</div>
				</div>

				<div class="mb-5">
					<label for class="vs-input--label">
						{{ $t('ProfileDormitory.PermanentAddress') }}
						<span class="text-danger">*</span>:
					</label>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap:2 sm:gap-4">
						<div>
							<v-select
								:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.State').toLowerCase()}`"
								v-model="localData.personal_info.permanent_address.state"
								:reduce="state => state.name"
								label="name"
								name="permanent_address_state"
								class="mb-3 sm:mb-0"
								v-validate="'required'"
								:options="this.$store.state.states"
								@input="handleStateChange"
								:data-vv-as="$t('ProfileDormitory.State')"
							>
								<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
							</v-select>
							<span class="text-danger text-sm" v-show="errors.has('permanent_address_state')">{{ errors.first("permanent_address_state") }}</span>
						</div>
						<div>
							<v-select
								:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.District').toLowerCase()}`"
								v-model="localData.personal_info.permanent_address.district"
								:reduce="district => district.name"
								label="name"
								name="permanent_address_district"
								class="mb-3 sm:mb-0"
								v-validate="'required'"
								:options="this.$store.state.districts"
								@input="handleDistrictChange"
								:data-vv-as="$t('ProfileDormitory.District')"
							>
								<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
							</v-select>
							<span class="text-danger text-sm" v-show="errors.has('permanent_address_district')">{{ errors.first("permanent_address_district") }}</span>
						</div>
						<div>
							<v-select
								:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.Ward').toLowerCase()}`"
								v-model="localData.personal_info.permanent_address.ward"
								:reduce="ward => ward.name"
								label="name"
								name="permanent_address_ward"
								class="mb-3 sm:mb-0"
								v-validate="'required'"
								:options="this.$store.state.wards"
								:data-vv-as="$t('ProfileDormitory.Ward')"
							>
								<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
							</v-select>
							<span class="text-danger text-sm" v-show="errors.has('permanent_address_ward')">{{ errors.first("permanent_address_ward") }}</span>
						</div>
						<div>
							<vs-input
								:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.DetailAddress')}`"
								v-model="localData.personal_info.permanent_address.detail"
								class="w-full"
								name="permanent_address_detail"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.DetailAddress')"
							/>
							<span class="text-danger text-sm" v-show="errors.has('permanent_address_detail')">{{ errors.first("permanent_address_detail") }}</span>
						</div>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 gap:2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Ethnicity') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.Ethnicity').toLowerCase()}`"
							v-model="localData.personal_info.ethnicity"
							name="ethnicity"
							class="w-full"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Ethnicity')"
							:options="choices.ethnicities"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('ethnicity')">{{ errors.first("ethnicity") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Religion') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.Religion').toLowerCase()}`"
							v-model="localData.personal_info.religion"
							name="religion"
							class="w-full"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Religion')"
							:options="choices.religions"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('religion')">{{ errors.first("religion") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.IdCardNumber') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.IdCardNumber')}`"
							v-model="localData.personal_info.id_card.number"
							class="w-full"
							name="idcardnumber"
							v-validate="'required|regex:^\\d{12}$'"
							:data-vv-as="$t('ProfileDormitory.IdCardNumber')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('idcardnumber')">{{ errors.first("idcardnumber") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.IdCardIssueDate') }}
							<span class="text-danger">*</span>:
						</label>
						<vx-input-group>
							<template slot="append">
								<div class="append-text text-black">
									<i class="nucleo nucleo-calendar-date" @click="triggerClickIdCardIssueDatePicker"></i>
								</div>
							</template>

							<cbs-date-picker
								clearable
								class="w-full"
								name="idcardissuedate"
								prefix-icon="''"
								ref="idCardIssueDatePickerRef"
								:format="userDateFormat"
								value-format="yyyy-MM-dd"
								:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.IdCardIssueDate').toLowerCase()}`"
								v-model="localData.personal_info.id_card.issue_date"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.DateOfBirth')"
							/>
						</vx-input-group>
						<span class="text-danger text-sm" v-show="errors.has('idcardissuedate')">{{ errors.first("idcardissuedate") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.IdCardIssuePlace') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.IdCardIssuePlace').toLowerCase()}`"
							v-model="localData.personal_info.id_card.issue_place"
							name="idcardissueplace"
							class="w-full"
							v-validate="'required'"
							:options="choices.idCardIssuePlaces"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('idcardissueplace')">{{ errors.first("idcardissueplace") }}</span>
					</div>
				</div>
				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.PersonalPhone') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.PersonalPhone')}`"
							v-model="localData.personal_info.personal_phone"
							class="w-full"
							name="personalphone"
							v-validate="{ required: true, regex: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/ }"
							:data-vv-as="$t('ProfileDormitory.PersonalPhone')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('personalphone')">{{ errors.first("personalphone") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.PersonalEmail') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.PersonalEmail')}`"
							v-model="localData.personal_info.personal_email"
							type="email"
							class="w-full"
							name="personalemail"
							v-validate="'required|email'"
							:data-vv-as="$t('ProfileDormitory.PersonalEmail')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('personalemail')">{{ errors.first("personalemail") }}</span>
					</div>
				</div>
			</div>
		</div>

		<div class="bg-gray-500 p-1 my-4">
			<div class="bg-white p-4 border rounded-b">
				<div class="font-bold text-center mb-3">
					{{ $t('ProfileDormitory.Relatives') }}
				</div>
				<div style="background: #f8f8f8" class="rounded-sm sm:p-4 p-2 sm:mb-4 mb-2" v-for="(relative, index) in localData.personal_info.relatives" :key="index">
					<div class="font-bold mb-3">
						{{ $t('ProfileDormitory.Relatives') + ' ' + (index + 1) }}
					</div>
					<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
						<div class="mb-5">
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.Relationship') }}
								<span class="text-danger">*</span>:
							</label>
							<v-select
								:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.Relationship').toLowerCase()}`"
								v-model="relative.relationship"
								:name="`relationship_${index}`"
								class="w-full bg-white"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.Relationship')"
								:options="choices.relationships"
							>
								<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
							</v-select>
							<span class="text-danger text-sm" v-show="errors.has(`relationship_${index}`)">{{ errors.first(`relationship_${index}`) }}</span>
						</div>

						<div class="mb-5">
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.RelativeName') }}
								<span class="text-danger">*</span>:
							</label>
							<vs-input
								:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.RelativeName')}`"
								v-model="relative.name"
								class="w-full"
								:name="`relativename_${index}`"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.RelativeName')"
							/>
							<span class="text-danger text-sm" v-show="errors.has(`relativename_${index}`)">{{ errors.first(`relativename_${index}`) }}</span>
						</div>
					</div>

					<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
						<div class="mb-5">
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.RelativeBirth') }}
								<span class="text-danger">*</span>:
							</label>
							<vs-input
								type="number"
								:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.RelativeBirth')}`"
								v-model="relative.year_of_birth"
								class="w-full"
								:name="`relativebirth_${index}`"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.RelativeBirth')"
							/>
							<span class="text-danger text-sm" v-show="errors.has(`relativebirth_${index}`)">{{ errors.first(`relativebirth_${index}`) }}</span>
						</div>

						<div class="mb-5">
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.RelativePhone') }}
								<span class="text-danger">*</span>:
							</label>
							<vs-input
								:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.RelativePhone')}`"
								v-model="relative.phone"
								class="w-full"
								:name="`relativephone_${index}`"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.RelativePhone')"
							/>
							<span class="text-danger text-sm" v-show="errors.has(`relativephone_${index}`)">{{ errors.first(`relativephone_${index}`) }}</span>
						</div>
					</div>

					<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
						<div class="mb-5">
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.RelativeOccupation') }}
								<span class="text-danger">*</span>:
							</label>
							<vs-input
								:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.RelativeOccupation')}`"
								v-model="relative.occupation"
								class="w-full"
								:name="`occupation_${index}`"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.RelativeOccupation')"
							/>
							<span class="text-danger text-sm" v-show="errors.has(`occupation_${index}`)">{{ errors.first(`occupation_${index}`) }}</span>
						</div>

						<div class="mb-5">
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.RelativeIncome') }}
								<span class="text-danger">*</span>:
							</label>
							<vs-input
								type="number"
								:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.RelativeIncome')}`"
								v-model="relative.income"
								class="w-full"
								:name="`income_${index}`"
								v-validate="'required'"
								:data-vv-as="$t('ProfileDormitory.RelativeIncome')"
							/>
							<span class="text-danger text-sm" v-show="errors.has(`income_${index}`)">{{ errors.first(`income_${index}`) }}</span>
						</div>
					</div>
					<div class="flex items-center justify-end">
						<vs-button
							radius
							type="flat"
							class="p-0"
							color="primary"
							icon-pack="nucleo"
							icon="nucleo-trash-can"
							@click="deleteRelative(index)"
						></vs-button>
					</div>
				</div>
				<div class="flex flex-wrap items-center justify-center">
					<vs-button class="w-full sm:w-2/3 sm:max-w-xs mt-4 font-bold" @click="addRelative()">{{ $t('ProfileDormitory.AddRelative') + (localData.personal_info.relatives.length + 1) }}</vs-button>
				</div>
			</div>
		</div>

		<div class="bg-gray-500 p-1 my-4">
			<div class="bg-white p-4 border rounded-b">
				<div class="font-bold text-center mb-3">
					{{ $t('ProfileDormitory.StudentInfo') }}
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Room') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Room')}`"
							:value="$store.state.AppActiveUser.role.department.name"
							disabled
							class="w-full"
							name="room"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Room')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('room')">{{ errors.first("room") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Course') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Course')}`"
							v-model="localData.student_info.course"
							class="w-full"
							name="course"
							v-validate="'required|integer|min_value:5|max_value:9'"
							:data-vv-as="$t('ProfileDormitory.Course')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('course')">{{ errors.first("course") }}</span>
					</div>

				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.School') }}
							<span class="text-danger">*</span>:
						</label>
						<v-select
							:placeholder="`${$t('Actions.Choose')} ${$t('ProfileDormitory.School').toLowerCase()}`"
							v-model="localData.student_info.school"
							:name="`school`"
							label="name"
							class="w-full bg-white"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.School')"
							:options="choices.universities"
						>
							<span slot="no-options">{{ $t('Notify.NoMatchingOptions') }}</span>
						</v-select>
						<span class="text-danger text-sm" v-show="errors.has('school')">{{ errors.first("school") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.UniversityId') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.UniversityId')}`"
							v-model="localData.student_info.university_id"
							class="w-full"
							name="universityid"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.UniversityId')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('universityid')">{{ errors.first("universityid") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.StudentEmail') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.StudentEmail')}`"
							v-model="localData.student_info.student_email"
							class="w-full"
							name="studentemail"
							v-validate="'required|email'"
							:data-vv-as="$t('ProfileDormitory.StudentEmail')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('studentemail')">{{ errors.first("studentemail") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Faculty') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Faculty')}`"
							v-model="localData.student_info.faculty"
							class="w-full"
							name="faculty"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Faculty')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('faculty')">{{ errors.first("faculty") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Major') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Major')}`"
							v-model="localData.student_info.major"
							class="w-full"
							name="major"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Major')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('major')">{{ errors.first("major") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Specialty') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Specialty')}`"
							v-model="localData.student_info.specialty"
							class="w-full"
							name="specialty"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Specialty')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('specialty')">{{ errors.first("specialty") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Class') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Class')}`"
							v-model="localData.student_info.class"
							class="w-full"
							name="class"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Class')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('class')">{{ errors.first("class") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.AccountNumber') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.AccountNumber')}`"
							v-model="localData.student_info.bank_account.account_number"
							class="w-full"
							name="accountnumber"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.AccountNumber')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('accountnumber')">{{ errors.first("accountnumber") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.BankName') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.BankName')}`"
							v-model="localData.student_info.bank_account.bank_name"
							class="w-full"
							name="bankname"
							disabled
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.BankName')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('bankname')">{{ errors.first("bankname") }}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.Branch') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.Branch')}`"
							v-model="localData.student_info.bank_account.branch"
							class="w-full"
							name="branch"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.Branch')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('branch')">{{ errors.first("branch") }}</span>
					</div>

					<div class="mb-5">
						<label for class="vs-input--label">
							{{ $t('ProfileDormitory.AccountHolder') }}
							<span class="text-danger">*</span>:
						</label>
						<vs-input
							:placeholder="`${$t('Actions.Input')} ${$t('ProfileDormitory.AccountHolder')}`"
							v-model="localData.student_info.bank_account.account_holder"
							class="w-full"
							name="accountholder"
							v-validate="'required'"
							:data-vv-as="$t('ProfileDormitory.AccountHolder')"
						/>
						<span class="text-danger text-sm" v-show="errors.has('accountholder')">{{ errors.first("accountholder") }}</span>
					</div>
				</div>
			</div>
		</div>

		<div v-if="isChanged" class="flex flex-wrap items-center justify-center">
			<vs-button class="w-full sm:w-1/3 sm:max-w-xs mt-4 font-bold" @click="updateEmployee">{{ $t('Actions.SaveChanges') }}</vs-button>
		</div>
	</div>
</template>

<script>
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";
import vSelect from "vue-select";

export default {
	components: {
		vSelect,
		CbsUploadRef
	},
	created() {
		this.$store.dispatch("fetchStates");
	},
	data() {
		return {
			originalData: this.$constant.INIT_STUDENT_DATA,
			localData: Array.isArray(JSON.parse(this.$store.state.AppActiveUser.role.properties)) ? this.$constant.INIT_STUDENT_DATA : JSON.parse(this.$store.state.AppActiveUser.role.properties),
			choices: {
				genders: [ "Nam", "Nữ" ],
				religions: [
					'Không có', 'Phật giáo', 'Công giáo', 'Tin lành', 'Cao đài', 'Phật giáo Hòa Hảo',
					'Hồi giáo', 'Baha’i giáo', 'Tịnh độ Cư sỹ Phật hội', 'Cơ đốc Phục lâm',
					'Phật giáo Tứ Ân Hiếu nghĩa', 'Minh Sư đạo', 'Minh lý đạo', 'Bà la môn giáo', 'Mặc môn',
					'Phật giáo Hiếu Nghĩa Tà Lơn', 'Bửu Sơn Kỳ Hương'
				],
				ethnicities: [
					'Kinh', 'Tày', 'Thái', 'Mường', 'Hoa', 'Khmer', 'Nùng', 'Hmong', 'Dao',
					'Gia Rai', 'Ngái', 'Ê Đê', 'Ba Na', 'Xơ Đăng', 'Sán Chay', 'Cơ Ho', 'Chăm',
					'Sán Dìu', 'Hrê', 'Mnông', 'Ra Glai', 'Xtiêng', 'Bru-Vân Kiều', 'Thổ',
					'Giáy', 'Cơ Tu', 'Gié-Triêng', 'Mạ', 'Khơ Mú', 'Co', 'Tà Ôi', 'Chơ Ro',
					'Kháng', 'Xinh Mun', 'Hà Nhì', 'Chu Ru', 'Lào', 'La Chí', 'La Ha',
					'Phù Lá', 'La Hủ', 'Lự', 'Lô Lô', 'Chứt', 'Mảng', 'Pà Thẻn', 'Co Lao',
					'Cống', 'Bố Y', 'Si La', 'Pu Péo', 'Brâu', 'Ơ Đu', 'Rơ Măm', 'Người nước ngoài'
				],
				idCardIssuePlaces: [
					"Cục Cảnh sát quản lý hành chính về trật tự xã hội",
					"Cục Cảnh sát ĐKQL cư trú và DLQG về dân cư"
				],
				relationships: [
					'Ông nội', 'Bà nội', 'Ông ngoại', 'Bà ngoại', 'Bố', 'Mẹ', 'Anh trai', 'Chị gái',
					'Em trai', 'Em gái', 'Chú', 'Cô', 'Bác trai', 'Bác gái', 'Dì', 'Cậu', 'Cháu trai',
					'Cháu gái', 'Con trai', 'Con gái', 'Vợ', 'Chồng', 'Bố chồng', 'Mẹ chồng', 'Bố vợ',
					'Mẹ vợ', 'Con dâu', 'Con rể', 'Anh rể', 'Chị dâu', 'Em dâu', 'Em rể', 'Bà thím',
					'Ông thím', 'Ông dượng', 'Bà dượng', 'Cháu nội', 'Cháu ngoại', 'Cháu trai họ',
					'Cháu gái họ', 'Con nuôi'
				],
				universities: [
					{ id: 1, name: "ĐH An ninh nhân dân", abbreviations: "T47/T04" },
					{ id: 2, name: "ĐH Bách khoa (ĐHQG TP.HCM)", abbreviations: "HCMUT" },
					{ id: 3, name: "ĐH Cảnh sát nhân dân", abbreviations: "T48/T05" },
					{ id: 4, name: "ĐH Công nghiệp TP.HCM", abbreviations: "IUH" },
					{ id: 5, name: "ĐH Công nghệ Thông tin (ĐHQG TP.HCM)", abbreviations: "UIT" },
					{ id: 6, name: "ĐH Công Thương TP.HCM", abbreviations: "HUIT" },
					{ id: 7, name: "ĐH Giao thông Vận tải TP.HCM", abbreviations: "UTH" },
					{ id: 8, name: "ĐH Giao thông Vận tải - Phân hiệu TP.HCM", abbreviations: "UTC2" },
					{ id: 9, name: "ĐH Khoa học Tự nhiên (ĐHQG TP.HCM)", abbreviations: "HCMUS" },
					{ id: 10, name: "ĐH Khoa học Xã hội và Nhân văn (ĐHQG TP.HCM)", abbreviations: "HCMUSSH" },
					{ id: 11, name: "ĐH Kinh tế – Luật (ĐHQG TP.HCM)", abbreviations: "HCMUEL" },
					{ id: 12, name: "ĐH Kiến trúc TP.HCM", abbreviations: "UAH" },
					{ id: 13, name: "ĐH Lao động – Xã hội (Cơ sở 2)", abbreviations: "ULSA2" },
					{ id: 14, name: "ĐH Luật TP.HCM", abbreviations: "ULAW" },
					{ id: 15, name: "ĐH Mở TP.HCM", abbreviations: "OU" },
					{ id: 16, name: "ĐH Mỹ thuật TP.HCM", abbreviations: "MT" },
					{ id: 17, name: "ĐH Ngoại thương (Cơ sở 2)", abbreviations: "FTU2" },
					{ id: 18, name: "ĐH Ngân hàng TP.HCM", abbreviations: "HUB" },
					{ id: 19, name: "ĐH Nông Lâm TP.HCM", abbreviations: "NLU" },
					{ id: 20, name: "ĐH Quốc tế (ĐHQG TP.HCM)", abbreviations: "HCMIU" },
					{ id: 21, name: "ĐH Sài Gòn", abbreviations: "SGU" },
					{ id: 22, name: "ĐH Sân khấu – Điện ảnh TP.HCM", abbreviations: "SKDAHCM" },
					{ id: 23, name: "ĐH Sư phạm Kỹ thuật TP.HCM", abbreviations: "HCMUTE" },
					{ id: 24, name: "ĐH Sư phạm Thể dục Thể thao", abbreviations: "UPES" },
					{ id: 25, name: "ĐH Sư phạm TP.HCM", abbreviations: "HCMUE" },
					{ id: 26, name: "ĐH Thể dục Thể thao TP. HCM", abbreviations: "USH" },
					{ id: 27, name: "Phân hiệu Trường ĐH Thủy lợi", abbreviations: "TLUS" },
					{ id: 28, name: "ĐH Trần Đại Nghĩa", abbreviations: "TDNU" },
					{ id: 29, name: "ĐH Tài chính – Marketing", abbreviations: "UFM" },
					{ id: 30, name: "ĐH Tài nguyên và Môi trường", abbreviations: "HCMUNRE" },
					{ id: 31, name: "ĐH Tôn Đức Thắng", abbreviations: "TDTU" },
					{ id: 32, name: "ĐH Văn hóa TP.HCM", abbreviations: "HUC" },
					{ id: 33, name: "ĐH Việt - Đức", abbreviations: "VGU" },
					{ id: 34, name: "ĐH Y Dược TP.HCM", abbreviations: "UMP" },
					{ id: 35, name: "ĐH Y khoa Phạm Ngọc Thạch", abbreviations: "PNT" },
					{ id: 36, name: "Khoa Y (ĐHQG TP.HCM)", abbreviations: "QSY" },
					{ id: 37, name: "Khoa Chính trị - Hành chính (ĐHQG TP.HCM)", abbreviations: "SPAS" },
					{ id: 38, name: "ĐH Lao động - Xã hội (CSII)", abbreviations: "ULSA2" },
					{ id: 39, name: "HV Cán bộ Thành phố Hồ Chí Minh", abbreviations: "HCA" },
					{ id: 40, name: "HV Công nghệ Bưu chính Viễn thông (cơ sở 2)", abbreviations: "PTIT" },
					{ id: 41, name: "HV Hàng không Việt Nam", abbreviations: "VAA" },
					{ id: 42, name: "Phân viện HV Hành chính Quốc Gia (Việt Nam) khu vực Miền Nam (tại thành phố Hồ Chí Minh - Sài Gòn)", abbreviations: "NAPA" },
					{ id: 43, name: "HV Kỹ thuật Mật mã cơ sở phía Nam", abbreviations: "ACT" },
					{ id: 44, name: "HV Kỹ thuật Quân sự (cơ sở 2)", abbreviations: "MTA" },
					{ id: 45, name: "Nhạc viện TP.HCM", abbreviations: "HCMCONS" },
					{ id: 46, name: "HV Thanh thiếu niên Việt Nam phân viện khu vực Miền Nam", abbreviations: "VYA" },
					{ id: 47, name: "ĐH Kinh tế TP.HCM", abbreviations: "UEH" }
				]
			}
		};
	},
	methods: {
		async getStudentData() {
			try {
				this.showLoading();
				this.localData = JSON.parse(this.$store.state.AppActiveUser.role.properties);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.hideLoading());
			}
		},
		capitalizeWords(str) {
			return str
				.replace(/\s+/g, ' ') // Đảm bảo mỗi từ chỉ có 1 khoảng trắng giữa chúng
				.split(' ') // Tách các từ thành mảng
				.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Viết hoa chữ cái đầu, các chữ cái sau viết thường
				.join(' '); // Ghép lại thành chuỗi với 1 khoảng trắng giữa các từ
		},
		triggerClickDateOfBirthPicker() {
			this.$refs.dateOfBirthPickerRef.$children[0].pickerVisible = true;
		},
		triggerClickIdCardIssueDatePicker() {
			this.$refs.idCardIssueDatePickerRef.$children[0].pickerVisible = true;
		},
		handleStateChange(selectedState) {
			const stateId = this.$store.state.states.find(state => state.name === selectedState).id;
			this.$store.dispatch("fetchDistricts", { state_id: stateId });
		},
		handleDistrictChange(selectedDistrict) {
			const districtId = this.$store.state.districts.find(district => district.name === selectedDistrict).id;
			this.$store.dispatch("fetchWards", { district_id: districtId });
		},
		addRelative() {
			this.localData = {
				...this.localData,
				personal_info: {
					...this.localData.personal_info,
					relatives: [
						...this.localData.personal_info.relatives,
						{
							relationship: "",
							name: "",
							year_of_birth: "",
							phone: "",
							occupation: "",
							income: "",
						}
					]
				}
			};
		},
		deleteRelative(index) {
			this.localData = {
				...this.localData,
				personal_info: {
					...this.localData.personal_info,
					relatives: this.localData.personal_info.relatives.filter((_, i) => i !== index)
				}
			};
		},
		updateEmployee() {
			this.$validator
				.validateAll()
				.then(result => {
					if (result) {
						const properties = {
							...this.localData,
							personal_info: {
								...this.localData.personal_info,
								full_name: this.capitalizeWords(this.localData.personal_info.full_name),
								personal_email: this.localData.personal_info.personal_email.toLowerCase(),
								permanent_address: {
									...this.localData.personal_info.permanent_address,
									detail: this.capitalizeWords(this.localData.personal_info.permanent_address.detail),
								},
							},
							student_info: {
								...this.localData.student_info,
								faculty: this.capitalizeWords(this.localData.student_info.faculty),
								major: this.capitalizeWords(this.localData.student_info.major),
								specialty: this.capitalizeWords(this.localData.student_info.specialty),
								student_email: this.localData.student_info.student_email.toLowerCase(),
								room: this.$store.state.AppActiveUser.role.department.name,
							},
						};
						const payload = {
							id: this.$store.state.AppActiveUser.role.id,
							name: this.$store.state.AppActiveUser.role.name,
							properties: JSON.stringify(properties)
						};
						this.$store.dispatch("auth/updateEmployee", payload).then(res => {
							const notifyOptions = {
								context: this,
								options: {
									title: this.$t('ProfileDormitory.UpdateStudentInfo'),
									text: res.update_employee ? this.$t('ProfileDormitory.UpdateStudentInfoSuccessful') : this.$t('ProfileDormitory.UpdateStudentInfoFailed')
								}
							};
							res.update_employee ? this.$helper.notifySuccess(notifyOptions) : this.$helper.notifyError(notifyOptions);
						});
					}
				})
				.catch(() => {
					this.$helper.notifyError({
						context: this,
						options: {
							title: this.$t('ProfileDormitory.UpdateStudentInfo'),
							text: this.$t('ProfileDormitory.UpdateStudentInfoFailed')
						}
					});
				});
		},
		haveIdenticalIds(array1, array2) {
			const ids1 = array1.map(item => item.id).sort();
			const ids2 = array2.map(item => item.id).sort();

			if (ids1.length !== ids2.length) {
				return false;
			}

			for (let i = 0; i < ids1.length; i++) {
				if (ids1[i] !== ids2[i]) {
					return false;
				}
			}

			return true;
		},
		isChanged() {
			const original = this.originalData;
			const current = this.localData;
			return JSON.stringify(original) != JSON.stringify(current);

		},
	}
};
</script>

<style lang="scss">
.semester-config {
	border-radius: 4px;
	background: #f8f8f8;
}
</style>
